import { Trans, useTranslation } from 'react-i18next';
import classNames from 'classnames';

import ProgressProducts from 'pages/dashboard/components/Progress/components/ProgressProducts';
import { getActiveProductsTypeCodes } from 'pages/dashboard/components/Progress/selectors';
import BadgeConnector from 'pages/dashboard/components/Connectors/BadgeConnector';
import { getOnboardingTabs, hasPermission } from 'pages/dashboard/selectors';
import { CommunicationIcon, FinanceIcon } from 'components/icons';
import { OnboardingTab } from 'pages/dashboard/utils';
import { ProductTypeCodes } from 'entities/progress';
import { BankAccount } from 'entities/bankAccounts';
import { ErpAccount } from 'entities/accounting';
import { Permissions } from 'entities/dashboard';
import Accordion from 'components/Accordion';
import { useAppSelector } from 'hooks';
import {
  getBankAccounts,
  getConnectedBankAccount,
} from 'pages/dashboard/components/BankAccounts/selectors';
import {
  getErpAccounts,
  getConnectedErpAccount,
} from 'pages/dashboard/components/AccountingSoftware/selectors';

const Confirmation = () => {
  const { t } = useTranslation('dashboard');

  const bankAccounts: BankAccount[] = useAppSelector(getBankAccounts);
  const erpAccounts: ErpAccount[] = useAppSelector(getErpAccounts);
  const tabList = useAppSelector(getOnboardingTabs);
  const connectedErpAccount: ErpAccount = useAppSelector(
    getConnectedErpAccount,
  );
  const connectedBankAccount = useAppSelector(getConnectedBankAccount);
  const activeProductTypes = useAppSelector(getActiveProductsTypeCodes);
  const bankPermissions = useAppSelector((state) =>
    hasPermission(state, Permissions.bankIntegration),
  );
  const erpPermissions = useAppSelector((state) =>
    hasPermission(state, Permissions.erpIntegration),
  );

  const bankIncomplete = bankAccounts.length === 0 && bankPermissions.view;
  const erpIncomplete = erpAccounts.length === 0 && erpPermissions.view;

  const tabs = tabList.filter(
    (tab: OnboardingTab) =>
      tab.progress?.progress !== undefined &&
      (!tab.products ||
        tab.products.some((p) => activeProductTypes.includes(p))),
  );

  return (
    <div className="p-6">
      <p className="heading-2xl mb-1 font-bold">
        {t(
          `dashboard:submitModal.${
            activeProductTypes.includes(ProductTypeCodes.capital)
              ? 'confirmTitleCapital'
              : 'confirmTitle'
          }`,
        )}
      </p>
      <p className="text-secondary mb-9">
        {t(
          `dashboard:submitModal.${
            activeProductTypes.includes(ProductTypeCodes.capital)
              ? 'confirmTitleInfoCapital'
              : 'confirmTitleInfo'
          }`,
        )}
      </p>
      {activeProductTypes.includes(ProductTypeCodes.payments) &&
        (bankIncomplete || erpIncomplete) && (
          <>
            <p className="heading-lg mb-1">
              {t('dashboard:submitModal.confirmSubTitle')}
            </p>
            <p className="text-secondary pb-5 border-b mb-4">
              {bankIncomplete && erpIncomplete ? (
                <Trans
                  ns="dashboard"
                  i18nKey="submitModal.confirmSubTitleNotConnectedBoth"
                >
                  Your&nbsp;
                  <span className="font-semibold text-gray-900">
                    Accounting Software
                  </span>
                  &nbsp;and&nbsp;
                  <span className="font-semibold text-gray-900">
                    Bank Account
                  </span>
                  &nbsp;are not connected.
                </Trans>
              ) : (
                (bankIncomplete || erpIncomplete) && (
                  <Trans
                    ns="dashboard"
                    i18nKey="submitModal.confirmSubTitleNotConnected"
                    values={{
                      connector: t(
                        `dashboard:connectors.${
                          bankIncomplete ? 'bank' : 'accounting'
                        }.title`,
                      ),
                    }}
                  >
                    Your&nbsp;
                    <span className="font-semibold text-gray-900">
                      connector
                    </span>
                    &nbsp;is not connected.
                  </Trans>
                )
              )}

              <span className="ml-1">
                {t('dashboard:submitModal.confirmSubTitleInfo')}
              </span>
            </p>
          </>
        )}

      {activeProductTypes.includes(ProductTypeCodes.payments) &&
        (erpPermissions.view || bankPermissions.view) && (
          <div className="flex items-center">
            {erpPermissions.view && (
              <div className="mr-4">
                <BadgeConnector
                  image={
                    <CommunicationIcon className="w-6 h-6 text-blue-600" />
                  }
                  text={t('dashboard:connectors.accounting.title')}
                  connected={!!connectedErpAccount}
                  started={erpAccounts.length > 0}
                />
              </div>
            )}

            {bankPermissions.view && (
              <BadgeConnector
                image={<FinanceIcon className="w-6 h-6 text-blue-600" />}
                text={
                  bankAccounts.length > 1
                    ? t('dashboard:connectors.bank.multiTitle', {
                        value: bankAccounts.length,
                      })
                    : t('dashboard:connectors.bank.title')
                }
                connected={!!connectedBankAccount}
                started={bankAccounts.length > 0}
              />
            )}
          </div>
        )}

      {activeProductTypes.includes(ProductTypeCodes.payments) && (
        <>
          <p className="heading-lg mt-9 mb-1">
            {t('dashboard:submitModal.confirmOrderTitle')}
          </p>
          <p className="text-secondary pb-5 border-b mb-4">
            {t('dashboard:submitModal.confirmOrderSubTitle')}
          </p>
        </>
      )}

      {!activeProductTypes.includes(ProductTypeCodes.capital) && (
        <div className="flex flex-wrap pb-3">
          <ProgressProducts filter={[ProductTypeCodes.payments]} inline />
        </div>
      )}

      {tabs.map((tab: OnboardingTab) => (
        <Accordion
          title={
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-gray-800 font-normal">{t(tab.name)}</span>
              </div>
              <span
                className={classNames(
                  tab.progress?.progress === 100
                    ? 'text-green-600 bg-green-100'
                    : 'text-gray-700 bg-gray-100',
                  'rounded-md px-4 py-1 mr-9 font-normal',
                )}
              >
                {t('dashboard:submitModal.complete', {
                  value: tab.progress?.progress,
                })}
              </span>
            </div>
          }
          opened={tabs.length === 1}
          key={tab.key}
          className="mt-6 border border-gray-300 rounded-md"
          titleClassName="py-3 px-[18px] border-b hover:bg-gray-50"
          toggleOnTitleClick
        >
          <div>{tab.component}</div>
        </Accordion>
      ))}
    </div>
  );
};

export default Confirmation;
